import { queryClient } from '@/serverStore';
import { ProjectId, ProjectRole, UserGroup, UserId, UserPermission } from '@clef/shared/types';
import UserAPI from '../../api/user_api';
import OrgAdminAPI from '../../api/org_admin_api';
import fetchHookFactory from './fetchHookFactory';
import { projectQueryKeys } from '@/serverStore/projects';

export const [useFetchUsersForProject, refreshUsersForProject] = fetchHookFactory(
  async (projectId: ProjectId) => {
    const result: UserGroup = await UserAPI.fetchUsers(undefined, projectId);
    return result;
  },
  'cache',
);

export const updateUserPermissionsApi = async (
  projectId: number,
  userPermissions: Array<{ id: UserId; permissions: UserPermission[]; isGroupUser?: boolean }>,
) => {
  const response = await OrgAdminAPI.updateUserPermissions(projectId, userPermissions);
  refreshUsersForProject({ keys: 'refresh-all' });
  return response;
};

export const transferProjectOwnershipApi = async (
  orgId: number,
  projectId: number,
  userId: UserId,
) => {
  const response = await OrgAdminAPI.transferProjectOwnership(projectId, userId);
  refreshUsersForProject({ keys: 'refresh-all' });
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.orgAdminList(orgId) });
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.detail(projectId) });
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.orgAdminDetail(projectId) });
  return response;
};

export const deleteUserFromProjectApi = async (
  orgId: number,
  projectId: number,
  userId: UserId,
) => {
  const response = await OrgAdminAPI.deleteUserFromProject(projectId, userId);
  refreshUsersForProject({ keys: 'refresh-all' });
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.detail(projectId) });
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.orgAdminList(orgId) });
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.orgAdminDetail(projectId) });
  return response;
};

export const deleteProjectApi = async (projectId: number, orgId: number) => {
  const response = await OrgAdminAPI.deleteProject(projectId);
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.list(orgId) });
  return response;
};

export const editMemberRole = async (
  orgId: number,
  projectId: number,
  userId: UserId,
  newMemberRole: ProjectRole,
) => {
  const response = await OrgAdminAPI.editMemberRole(projectId, userId, newMemberRole);
  refreshUsersForProject({ keys: 'refresh-all' });
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.detail(projectId) });
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.orgAdminList(orgId) });
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.orgAdminDetail(projectId) });
  return response;
};

export async function changeProjectVisibilityApi(
  orgId: number,
  projectId: number,
  isInviteOnly: boolean,
) {
  const response = await OrgAdminAPI.changeVisibility(projectId, isInviteOnly);
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.detail(projectId) });
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.orgAdminList(orgId) });
  queryClient.invalidateQueries({ queryKey: projectQueryKeys.orgAdminDetail(projectId) });
  return response;
}
